import Decimal from "decimal.js";
import { keys } from "ramda";
import type { toZod } from "tozod";
import { match } from "ts-pattern";
import {
  array,
  boolean,
  literal,
  nativeEnum,
  number,
  object,
  string,
  type ZodArray,
  type ZodBoolean,
  type ZodEffects,
  ZodIssueCode,
  type ZodNumber,
  type ZodOptional,
} from "zod";

import {
  type InputActivityInstanceInput,
  type InputActivityParameterInput,
  type InputConnector,
  type InputMetadata,
  type InputValueWrapperInput,
  type InputWorkflowConfigInput,
  type InputWorkflowEventInput,
  type InputWorkflowInput,
  RetentionRuleEnum,
  SupportedExecutorsEnum,
} from "@/graphql.ts";
import {
  type BooleanParamType,
  type DateParamType,
  type FileParamType,
  type NumberParamType,
  type OrganismParamType,
  type RegionParamType,
  type SampleParamType,
  type StringParamType,
  type TrackerParamType,
  type WorkflowActivityParameterFieldProps,
  ZodCustomErrorParameterCode,
} from "@/modules/workflow/utils/types.ts";
import type { ArrayType } from "@/types.js";

const metadata: toZod<InputMetadata> = object({
  position: object({
    x: number().int(),
    y: number().int(),
  }),
  width: number().positive().int(),
});

const __parameterValuesSchema: toZod<InputValueWrapperInput> = object({
  textValue: array(string()).min(1),
  numberValue: array(number()).min(1),
  booleanValue: boolean(),
  dateValue: string().min(1),
  fileId: array(number().int().positive("Restricted or deleted file.")).min(1),
  organismId: number().int().positive("Restricted or deleted content."),
  attributeId: number().int().positive(),
  sampleValue: object({
    attributeId: number().optional(),
    sampleFileMappings: array(
      object({
        sampleId: number().positive("Restricted or deleted samples."),
        fileIds: array(
          number().int().positive("Some of the files you provided for analysis have restricted access."),
        ).optional(),
      }),
    ).optional(),
  }),
  region: object({
    contigId: number(),
    range: object({
      start: number(),
      stop: number(),
    }).optional(),
  }),
}).partial();

// Just make sure that one key is given. Not more nor less.
export const parameterValuesSchema = __parameterValuesSchema.superRefine((val, ctx) => {
  if (keys(val).length !== 1) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: "Only one value per parameter must be provided",
    });
  }
});

const parameterSchema: toZod<InputActivityParameterInput> = object({
  identifier: string().min(1),
  staged: boolean().optional(),
  values: parameterValuesSchema.nullable().optional() as unknown as ZodOptional<typeof __parameterValuesSchema>, // because the superRefine breaks the typings
});

const activitySchema: toZod<InputActivityInstanceInput> = object({
  identifier: string().min(1),
  name: string().min(1),
  version: string().min(1),
  disabled: boolean().optional(),
  skipped: boolean().optional(),
  metadata: metadata,
  parameters: array(parameterSchema),
  executor: nativeEnum(SupportedExecutorsEnum).nullish(),
});

const connectorSchema: toZod<InputConnector> = object({
  source: string().min(1),
  output: string().min(1).optional(),
  target: string().min(1),
  input: string().min(1).optional(),
});

export const workflowConfigSchema: toZod<InputWorkflowConfigInput> = object({
  activities: array(activitySchema),
  connectors: array(connectorSchema),
  sampleTypeId: number().int().positive().optional(),
  defaultExecutor: nativeEnum(SupportedExecutorsEnum).nullish(),
});

export const workflowEventSchema: toZod<InputWorkflowEventInput> = object({
  config: workflowConfigSchema,
  description: string().optional(),
  name: string().trim().min(1, "A session name is required"),
  projectId: number().int().positive(),
  retentionRule: nativeEnum(RetentionRuleEnum),
  workflowEventId: number().int().positive().optional(),
  workflowId: number().int().positive().optional(),
}).strict();

export const newSessionSchema: toZod<
  Pick<InputWorkflowEventInput, "name" | "description" | "projectId" | "retentionRule" | "config" | "workflowId">
> = object({
  name: string().trim().min(1, "A session name is required"),
  description: string().optional().nullable(),
  projectId: number().int().positive(),
  retentionRule: nativeEnum(RetentionRuleEnum),
  config: workflowConfigSchema.optional(),
  workflowId: number().optional(),
});

export const newWorkflowTemplateSchema: toZod<Pick<InputWorkflowInput, "name" | "description" | "usergroupId">> =
  object({
    name: string().trim().min(1, "A template name is required"),
    description: string().optional(),
    usergroupId: number().int().nonnegative().optional(), // special case: is required, but can be undefined in case of private usergroup
  });

export const publishWorkflowSchema: toZod<InputWorkflowInput> = object({
  workflowId: number().int().positive().optional(),
  name: string().trim().min(1, "A workflow name is required"),
  description: string().trim().optional(),
  usergroupId: number().int().nonnegative().optional(),
  config: workflowConfigSchema,
});

export const generateValidationSchema = (props: WorkflowActivityParameterFieldProps) => {
  const parameterSchema = match(props)
    .with({ __typename: "StringParam" }, (parameter) => {
      let stringSchema = array(
        string({
          invalid_type_error: "Unknown ID.",
          required_error: "Your input is required.",
        }),
        {
          invalid_type_error: "Your input is required.",
          required_error: "Your input is required.",
        },
      );

      if (parameter.multiSelect !== true) {
        stringSchema = stringSchema.length(1, {
          message: "You must select only one option.",
        });
      }

      return stringSchema satisfies toZod<StringParamType>;
    })
    .with({ __typename: "NumberParam" }, (parameter) => {
      let typeSchema = number({
        invalid_type_error: "Required.",
        required_error: "Required.",
      }).finite({
        message: "Invalid number: Infinity.",
      });

      if (parameter.numberConstraints?.minValue != undefined) {
        typeSchema = typeSchema.min(parameter.numberConstraints?.minValue, {
          message: `The minimum value is ${parameter.numberConstraints?.minValue}.`,
        });
      }

      if (parameter.numberConstraints?.maxValue != undefined) {
        typeSchema = typeSchema.max(parameter.numberConstraints?.maxValue, {
          message: `The maximum value is ${parameter.numberConstraints?.maxValue}.`,
        });
      }

      // Decimal
      const precision = parameter.numberConstraints?.maxDecimals;

      if (precision != undefined) {
        typeSchema =
          precision === 0
            ? typeSchema.int("Must be an integer.")
            : (typeSchema.refine(
                (value: number) => {
                  const num = new Decimal(value);
                  return num.decimalPlaces() <= precision;
                },
                {
                  message: `The number may have at most ${precision} digits after the decimal.`,
                },
              ) as unknown as ZodNumber);
      }

      let numberSchema = array(typeSchema, {
        invalid_type_error: "Your input is required.",
        required_error: "Your input is required.",
      });

      if (parameter.multiSelect !== true) {
        numberSchema = numberSchema.length(1, {
          message: "You must select only one option.",
        });
      }

      return numberSchema satisfies toZod<NumberParamType>;
    })
    .with({ __typename: "BooleanParam" }, (parameter) => {
      const boolSchema = parameter.optional
        ? boolean({
            invalid_type_error: "Your input is required.",
            required_error: "Your input is required.",
          })
        : (literal(true, {
            errorMap: () => ({ message: "Must be checked." }),
          }) as unknown as ZodBoolean);
      return boolSchema satisfies toZod<BooleanParamType>;
    })
    .with({ __typename: "GenomeParam" }, () => {
      const genomeDatabaseOrFileSchema = number({
        required_error: "Your input is required.",
        invalid_type_error: "Your input is required.",
      }).refine((id) => id > 0, {
        message: "Restricted or deleted content.",
        params: { code: ZodCustomErrorParameterCode.Unavailable },
      });
      return genomeDatabaseOrFileSchema satisfies ZodEffects<toZod<OrganismParamType>>;
    })
    .with({ __typename: "TrackerParam" }, () => {
      const trackerSchema = number({
        invalid_type_error: "Your input is required.",
        required_error: "Your input is required.",
      }).positive();
      return trackerSchema satisfies toZod<TrackerParamType>;
    })
    .with({ __typename: "FileParam" }, (parameter) => {
      let fileSchema = array(
        number({
          invalid_type_error: "Unknown ID.",
          required_error: "Required.",
        }).refine((id) => id > 0, {
          message: "Restricted or deleted file.",
          params: { code: ZodCustomErrorParameterCode.Unavailable },
        }),
        {
          required_error: "Your input is required.",
          invalid_type_error: "Your input is required.",
        },
      );

      if (parameter.multiSelect !== true) {
        fileSchema = fileSchema.length(1, {
          message: "You must select only one option.",
        });
      }

      return fileSchema satisfies ZodArray<ZodEffects<toZod<ArrayType<FileParamType>>>>;
    })
    .with({ __typename: "RegionParam" }, () => {
      const regionSchema = object(
        {
          contigId: number(), // TODO: Should not be negative?
          range: object({
            start: number().min(1),
            stop: number().positive(),
          }).optional(),
        },
        {
          invalid_type_error: "Your input is required.",
          required_error: "Your input is required.",
        },
      );
      return regionSchema satisfies toZod<RegionParamType>;
    })
    .with({ __typename: "SampleParam" }, () => {
      let fileIdsSchema = array(
        number().refine((id) => id > 0, {
          message: "Some of the files you provided for analysis have restricted access.",
          params: { code: ZodCustomErrorParameterCode.Unavailable },
        }),
        {
          required_error: "Files are required for every selected sample.",
          invalid_type_error: "Files are required for every selected sample.",
        },
      ).min(1, { message: "Files are required for every selected sample." });

      if (props.optional) {
        fileIdsSchema = fileIdsSchema.min(0).optional();
      }

      let sampleMappingsSchema = array(
        object({
          sampleId: number().refine((id) => id > 0, {
            message: "Restricted or deleted samples.",
            params: { code: ZodCustomErrorParameterCode.Unavailable },
          }),
          fileIds: fileIdsSchema,
        }),
        {
          required_error: "A sample selection is required.",
          invalid_type_error: "A sample selection is required.",
        },
      ).min(1, { message: "A sample selection is required." });

      if (props.optional) {
        sampleMappingsSchema = sampleMappingsSchema.min(0).optional();
      }

      let attributeIdSchema = number({
        required_error: "Choose a sample attribute to provide files.",
      });

      if (props.optional) {
        attributeIdSchema = attributeIdSchema.optional();
      }

      const sampleSchema = object(
        {
          sampleFileMappings: sampleMappingsSchema,
          attributeId: attributeIdSchema,
        },
        {
          invalid_type_error: "Your input is required.",
          required_error: "Your input is required.",
        },
      );

      // FIXME Fix return type to match schema
      return sampleSchema satisfies toZod<SampleParamType>;
    })
    .with({ __typename: "DateParam" }, () => {
      const dateSchema = string({ required_error: "Your input is required." });
      return dateSchema satisfies toZod<DateParamType>;
    })
    .exhaustive();

  if (props.optional && parameterSchema) {
    return parameterSchema.nullish();
  }
  return parameterSchema;
};
